import React from 'react';
import { SControlBar, SControlButton, STimeDisplay, STimeDuration } from './styled';
import ProgressBar from './ProgressBar';
import SpeedSelector from './SpeedSelector';
import CaptionsToggle from './CaptionsToggle';
import VolumeControl from './VolumeControl';
import { formatTime } from './utils/formatTime';

interface IProps {
  visible: boolean;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  buffered: number;
  playbackRate: number;
  captionsEnabled: boolean;
  hasSubtitles: boolean;
  isFullscreen: boolean;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  onTogglePlay: () => void;
  onSeek: (time: number) => void;
  onChangeRate: (rate: number) => void;
  onToggleCaptions: () => void;
  onToggleFullscreen: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const Controls: React.FC<IProps> = ({
  visible,
  isPlaying,
  currentTime,
  duration,
  buffered,
  playbackRate,
  captionsEnabled,
  hasSubtitles,
  isFullscreen,
  videoRef,
  onTogglePlay,
  onSeek,
  onChangeRate,
  onToggleCaptions,
  onToggleFullscreen,
  onMouseEnter,
  onMouseLeave
}) => (
  <SControlBar
    $visible={visible}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    role="toolbar"
    aria-label="Video controls"
  >
    <SControlButton onClick={onTogglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        {isPlaying ? (
          <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
        ) : (
          <path d="M8 5v14l11-7z" />
        )}
      </svg>
    </SControlButton>

    <STimeDisplay>
      {formatTime(currentTime)}
      <STimeDuration>{` / ${formatTime(duration)}`}</STimeDuration>
    </STimeDisplay>

    <ProgressBar
      currentTime={currentTime}
      duration={duration}
      buffered={buffered}
      isPlaying={isPlaying}
      onSeek={onSeek}
    />

    <SpeedSelector playbackRate={playbackRate} onChangeRate={onChangeRate} />

    {hasSubtitles && (
      <CaptionsToggle enabled={captionsEnabled} onToggle={onToggleCaptions} />
    )}

    <VolumeControl videoRef={videoRef} />

    <SControlButton
      onClick={onToggleFullscreen}
      aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
    >
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        {isFullscreen ? (
          <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
        ) : (
          <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
        )}
      </svg>
    </SControlButton>
  </SControlBar>
);

export default Controls;
